import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../bizsuite-app';
import DataTable from '../components/DataTable';
import FormField from '../components/FormField';
import { toast } from '../utils/toast';

const SCHEMA_TYPES   = [
  { value:'Standard', label:'Standard' },
  { value:'GST',      label:'GST (India domestic)' },
  { value:'Export',   label:'Export (zero-rated)' },
  { value:'SEZ',      label:'SEZ' },
  { value:'Custom',   label:'Custom' },
];
const STATUS_OPTIONS = [
  { value:'Active',   label:'Active' },
  { value:'Inactive', label:'Inactive' },
  { value:'Draft',    label:'Draft' },
];
const CATEGORY_OPTIONS = [
  { value:'base',         label:'Base' },
  { value:'markup',       label:'Markup / Uplift' },
  { value:'discount',     label:'Discount' },
  { value:'tax',          label:'Tax (GST, VAT)' },
  { value:'withholding',  label:'Withholding Tax' },
  { value:'levy',         label:'Levy / Cess' },
  { value:'round_off',    label:'Round-off / Total' },
];
const CATEGORY_COLOR = {
  base:        { bg:'rgba(59,143,244,0.12)', color:'#3B8FF4' },
  markup:      { bg:'rgba(16,185,129,0.12)', color:'#10B981' },
  discount:    { bg:'rgba(239,68,68,0.12)',  color:'#EF4444' },
  tax:         { bg:'rgba(245,158,11,0.12)', color:'#F59E0B' },
  withholding: { bg:'rgba(244,114,182,0.12)',color:'#F472B6' },
  levy:        { bg:'rgba(191,95,255,0.12)', color:'#BF5FFF' },
  round_off:   { bg:'rgba(148,163,184,0.15)',color:'#94A3B8' },
};

// Per-component pricing classification. Drives which summary chip the
// component value flows into in the simulator and the contract Pricing
// Computation panel.
const PRICING_TYPE_OPTIONS = [
  { value:'base',         label:'Base' },
  { value:'indirect_tax', label:'Indirect Tax' },
  { value:'direct_tax',   label:'Direct Tax' },
  { value:'grand_total',  label:'Grand Total' },
];

// Sensible default Type when a row is created or migrated from a legacy
// row that has only a category. Lets the simulator keep working out of
// the box before the user explicitly picks a Type.
function defaultTypeFromCategory(cat) {
  switch (String(cat || '').toLowerCase()) {
    case 'base':        return 'base';
    case 'tax':
    case 'levy':        return 'indirect_tax';
    case 'withholding': return 'direct_tax';
    case 'round_off':   return 'grand_total';
    default:            return null;
  }
}

/* ─────────── formula evaluator ─────────── */
/* Conservative: only digits, whitespace, basic operators, word chars, parens. */
function evalFormula(expr, ctx) {
  if (!expr) return 0;
  if (!/^[\w\d\s+\-*/().,%?:><=!|&]*$/.test(expr)) return 0;
  try {
    // Component codes are normalised to lowercase when added to ctx, so
    // lowercase the formula expression before exec — this lets users type
    // `BASE * 0.09` or `base * 0.09` interchangeably without breaking the
    // formula. Numbers and symbols are unaffected by toLowerCase().
    const lowered = String(expr).toLowerCase();
    const names = Object.keys(ctx);
    const vals  = names.map(k => ctx[k]);
    // eslint-disable-next-line no-new-func
    const fn = new Function(...names, 'min', 'max', 'round', 'return (' + lowered + ');');
    const v = fn(...vals, Math.min, Math.max, Math.round);
    return Number.isFinite(Number(v)) ? Number(v) : 0;
  } catch (_) { return 0; }
}

/* Apply the schema in seq order against a base contract value.
 *
 * Resolver-first, formula-fallback:
 *   - If a Component Master rate (`resolvedById[component_id]`) exists,
 *     use that rate (preferring rate_pct, then fixed_value). This matches
 *     what the contract / invoice flow will actually compute.
 *   - Otherwise evaluate the component's formula against the running
 *     context (so subtotals and round-offs still work).
 *
 * The resolver is called without contract/customer context here, so only
 * Level 3 master rows can match. That's intentional — the editor preview
 * shouldn't pretend to know which jurisdiction or customer applies.
 */
function runSimulation(components, base, resolvedById = {}) {
  const ctx = { base: Number(base) || 0, contract_value: Number(base) || 0 };
  const ordered = [...components].sort((a,b) => (Number(a.seq)||0) - (Number(b.seq)||0));
  const results = [];
  for (const c of ordered) {
    const key = String(c.code || '').toLowerCase().replace(/[^a-z0-9_]/g, '_');
    const master = resolvedById[c.id];
    const hasMaster = master && !master.no_master &&
                      (master.rate_pct != null || master.fixed_value != null);

    let computed;
    let _source;
    let _matchLevel = null;
    if (hasMaster) {
      _source = 'master';
      _matchLevel = master.match_level;
      if (master.rate_pct != null) {
        computed = ctx.base * (Number(master.rate_pct) || 0) / 100;
      } else {
        computed = Number(master.fixed_value) || 0;
      }
    } else if (c.formula) {
      _source = 'formula';
      computed = evalFormula(c.formula, ctx);
    } else if (c.rate_pct) {
      _source = 'inline_pct';
      computed = ctx.base * (Number(c.rate_pct) || 0) / 100;
    } else if (c.fixed_value) {
      _source = 'inline_fixed';
      computed = Number(c.fixed_value) || 0;
    } else {
      _source = 'none';
      computed = 0;
    }
    if (key) ctx[key] = computed;
    results.push({ ...c, _computed: computed, _source, _matchLevel });
  }
  // Chip totals are now driven by each component's `pricing_type`. We sum
  // values into four buckets (base, indirect_tax, direct_tax, grand_total)
  // and fall back to category-based defaults for legacy rows that don't
  // yet have a Type set.
  //
  // Withholding-category components are AR/AP-side only — they don't flow
  // into the contract's billing total even if their type is set; the
  // simulator still surfaces them in the per-component breakdown so HR /
  // Finance can see the rate, but the chips skip them.
  const buckets = { base: 0, indirect_tax: 0, direct_tax: 0, grand_total: 0 };
  let billingTotal = 0;  // base + adders (markup/tax/levy) − discounts; ignores withholding
  for (const r of results) {
    const v = Number(r._computed) || 0;
    const t = r.pricing_type || defaultTypeFromCategory(r.category);
    if (t && buckets[t] !== undefined) buckets[t] += v;

    const isWithholding = r.category === 'withholding';
    if (isWithholding) continue;
    if (r.category === 'tax' || r.category === 'markup' || r.category === 'levy') {
      billingTotal += v;
    } else if (r.category === 'discount') {
      billingTotal -= v;
    } else if (r.category === 'base') {
      billingTotal += v;  // BASE component contributes once
    } else if (r.category === 'round_off' && r.formula) {
      // round_off with a formula overrides
      billingTotal = v;
    }
  }
  // If no row has category=base, fall back to ctx.base for the running total.
  if (!results.some(r => r.category === 'base')) billingTotal += ctx.base;

  return {
    results,
    totals: {
      base:         buckets.base || ctx.base,
      indirect_tax: buckets.indirect_tax,
      direct_tax:   buckets.direct_tax,
      grand_total:  buckets.grand_total || billingTotal,
      // legacy aliases for any callers still reading these names
      tax:          buckets.indirect_tax,
    },
  };
}

const dateOnly = (v) => {
  if (!v) return '';
  const s = String(v); return s.length >= 10 ? s.slice(0, 10) : s;
};

const fmtINR = (n) => {
  if (!Number.isFinite(Number(n))) return '—';
  return '₹ ' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

/* ─────────────────────────────── Main ─────────────────────────────── */

export default function PricingSchemas() {
  const [mode, setMode]             = useState('list');
  const [schemas, setSchemas]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [editRow, setEditRow]       = useState(null);
  const [components, setComponents] = useState([]);
  const [saving, setSaving]         = useState(false);
  const [err, setErr]               = useState('');
  const [search, setSearch]         = useState('');
  const [simBase, setSimBase]       = useState(1000000);
  const [showSim, setShowSim]       = useState(true);
  const [resolved, setResolved]     = useState([]);   // resolver output per component

  const fetchSchemas = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/api/pricing-schemas');
      if (r.ok) { const d = await r.json(); setSchemas(d.schemas || []); }
    } catch (_) {}
    setLoading(false);
  }, []);
  useEffect(() => { fetchSchemas(); }, [fetchSchemas]);

  function openNew() {
    setEditRow({ _new:true, code:'', name:'', description:'', schema_type:'Standard',
                 valid_from:'', valid_to:'', status:'Active' });
    setComponents([]); setErr(''); setMode('edit');
  }
  async function openEdit(row) {
    setErr('');
    try {
      const r = await apiFetch(`/api/pricing-schemas/${row.id}`);
      if (r.ok) {
        const d = await r.json();
        const schema = { ...d.schema, valid_from: dateOnly(d.schema.valid_from), valid_to: dateOnly(d.schema.valid_to) };
        setEditRow(schema);
        setComponents((d.components || []).map(c => ({ ...c, _key: c.id })));
      } else {
        setEditRow({ ...row, valid_from: dateOnly(row.valid_from), valid_to: dateOnly(row.valid_to) });
        setComponents([]);
      }
    } catch (_) {
      setEditRow({ ...row, valid_from: dateOnly(row.valid_from), valid_to: dateOnly(row.valid_to) });
      setComponents([]);
    }
    // Pull resolver output for this schema so the simulator below uses
    // master rates (where they exist) instead of just the formulas.
    try {
      const rr = await apiFetch(`/api/pricing-schemas/${row.id}/resolve`);
      if (rr.ok) { const dd = await rr.json(); setResolved(dd.components || []); }
      else setResolved([]);
    } catch (_) { setResolved([]); }
    setMode('edit');
  }

  async function handleSave() {
    if (!editRow) return;
    if (!editRow.code || !editRow.name) { setErr('Code and Name are required'); return; }
    setSaving(true); setErr('');
    try {
      let schemaId = editRow.id;
      if (editRow._new) {
        const r = await apiFetch('/api/pricing-schemas', { method:'POST', body: JSON.stringify(editRow) });
        if (!r.ok) { const d = await r.json(); setErr(d.error || 'Save failed'); setSaving(false); return; }
        const d = await r.json(); schemaId = d.id;
      } else {
        const r = await apiFetch(`/api/pricing-schemas/${editRow.id}`, { method:'PUT', body: JSON.stringify(editRow) });
        if (!r.ok) { const d = await r.json(); setErr(d.error || 'Save failed'); setSaving(false); return; }
      }
      const payload = components.map(c => ({
        ...c,
        seq:         c.seq === '' || c.seq === null ? null : Number(c.seq),
        rate_pct:    c.rate_pct === '' || c.rate_pct === null ? null : Number(c.rate_pct),
        fixed_value: c.fixed_value === '' || c.fixed_value === null ? null : Number(c.fixed_value),
      }));
      const r2 = await apiFetch(`/api/pricing-schemas/${schemaId}/components`, {
        method:'POST', body: JSON.stringify(payload),
      });
      if (!r2.ok) { const d = await r2.json(); setErr(d.error || 'Component save failed'); setSaving(false); return; }
      await fetchSchemas();
      setMode('list');
    } catch (e) { setErr('Network error: ' + e.message); }
    setSaving(false);
  }

  async function handleDelete(row) {
    if (!(await toast.confirm({ body: `Delete schema "${row.name}"? All components will be lost.`, danger: true }))) return;
    const r = await apiFetch(`/api/pricing-schemas/${row.id}`, { method:'DELETE' });
    if (r.ok) fetchSchemas(); else toast.error('Delete failed');
  }

  async function handleDuplicate(row) {
    const ok = await toast.confirm({
      title: 'Copy schema?',
      body: `This creates a new schema "${row.name} (Copy)" with all components and formulas duplicated. Status starts as Draft.`,
      confirmLabel: 'Copy schema',
    });
    if (!ok) return;
    const r = await apiFetch(`/api/pricing-schemas/${row.id}/duplicate`, { method: 'POST' });
    const d = await r.json().catch(() => ({}));
    if (r.ok) {
      toast.success(`Copied ${d.components_copied || 0} component(s) into "${d.name}"`);
      fetchSchemas();
    } else { toast.error(d.error || 'Copy failed'); }
  }

  let _key = 0;
  const newKey = () => '_new_' + Date.now() + '_' + (++_key);
  function addComponent() {
    setComponents(prev => [...prev, {
      _key:newKey(), seq:(prev.length+1)*10, code:'', name:'', formula:'',
      category:'tax', pricing_type:'indirect_tax',
      is_visible:true, is_taxable:false,
      rate_pct:'', fixed_value:'', debit_gl:'', credit_gl:'', notes:'',
    }]);
  }
  function updateComponent(idx, key, value) {
    setComponents(prev => { const copy=[...prev]; copy[idx] = { ...copy[idx], [key]:value }; return copy; });
  }
  function removeComponent(idx) { setComponents(prev => prev.filter((_,i) => i!==idx)); }
  function moveComponent(idx, dir) {
    setComponents(prev => {
      const j = idx + dir; if (j<0 || j>=prev.length) return prev;
      const copy = [...prev]; [copy[idx], copy[j]] = [copy[j], copy[idx]];
      copy.forEach((c,i) => { c.seq = (i+1)*10; });
      return copy;
    });
  }

  const resolvedById = useMemo(() => {
    const m = {};
    for (const r of resolved) m[r.component_id] = r;
    return m;
  }, [resolved]);
  const simulation = useMemo(
    () => runSimulation(components, simBase, resolvedById),
    [components, simBase, resolvedById]
  );

  const filteredSchemas = useMemo(() => {
    if (!search) return schemas;
    const s = search.toLowerCase();
    return schemas.filter(r =>
      (r.name||'').toLowerCase().includes(s) ||
      (r.code||'').toLowerCase().includes(s) ||
      (r.schema_type||'').toLowerCase().includes(s)
    );
  }, [schemas, search]);

  const columns = [
    { key:'code', label:'Code', sortable:true, width:'10%' },
    { key:'name', label:'Name', sortable:true, width:'28%' },
    { key:'schema_type', label:'Type', sortable:true, width:'10%',
      render: v => <span style={{ fontSize:11, padding:'2px 8px', borderRadius:4,
        background:'rgba(59,143,244,0.15)', color:'#3B8FF4' }}>{v}</span> },
    { key:'valid_from', label:'From', sortable:true, width:'10%',
      render: v => v ? new Date(v).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—' },
    { key:'valid_to',   label:'To',   sortable:true, width:'10%',
      render: v => v ? new Date(v).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—' },
    { key:'component_count', label:'Components', sortable:true, width:'10%', type:'number', align:'right' },
    { key:'contract_count',  label:'Contracts',  sortable:true, width:'9%',  type:'number', align:'right' },
    { key:'estimate_count',  label:'Estimates',  sortable:true, width:'9%',  type:'number', align:'right' },
    { key:'status', label:'Status', sortable:true, width:'8%',
      render: v => <span className={`badge ${v==='Active'?'badge-green':'badge-gray'}`}>{v}</span> },
  ];

  if (mode === 'edit' && editRow) {
    return <SchemaEditor
      row={editRow} setRow={setEditRow}
      components={components} setComponents={setComponents}
      err={err} saving={saving}
      onSave={handleSave} onCancel={() => setMode('list')}
      addComponent={addComponent} updateComponent={updateComponent}
      removeComponent={removeComponent} moveComponent={moveComponent}
      simulation={simulation}
      simBase={simBase} setSimBase={setSimBase}
      showSim={showSim} setShowSim={setShowSim}
    />;
  }

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', marginBottom:16, gap:12 }}>
        <input type="text" placeholder="Search by name, code, type..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex:1, maxWidth:400, padding:'8px 14px', borderRadius:6,
            border:'1px solid rgba(255,255,255,0.12)', background:'rgba(255,255,255,0.04)',
            color:'#E5E7EB', fontSize:13 }} />
        <button className="btn btn-primary" onClick={openNew}>+ New Schema</button>
        <button className="btn btn-secondary"
          onClick={() => { window.location.hash = '#/pricing-schemas/component-masters'; }}
          title="Maintain component-wise rates by region, customer region, and date range">
          Component Masters
        </button>
      </div>
      <DataTable columns={columns} data={filteredSchemas}
        actions={[
          { label:'Edit',   icon:'edit',   onClick:openEdit },
          { label:'Copy',   icon:'copy',   onClick:handleDuplicate },
          { label:'Delete', icon:'delete', onClick:handleDelete },
        ]}
        onRowClick={openEdit} loading={loading}
        emptyMessage="No pricing schemas yet. Click + New Schema to create one."
        emptyIcon={'\u{1F4B8}'} />
    </div>
  );
}

/* ═════════════════ Full-page editor ═════════════════ */

function SchemaEditor({
  row, setRow, components, setComponents, err, saving, onSave, onCancel,
  addComponent, updateComponent, removeComponent, moveComponent,
  simulation, simBase, setSimBase, showSim, setShowSim,
}) {
  const [detailsOpen, setDetailsOpen] = React.useState({});
  const toggleDetails = (k) => setDetailsOpen(s => ({ ...s, [k]: !s[k] }));
  // Accordion state for the schema-header card. Start open on new schemas
  // (user needs to fill it in); start folded on existing schemas so the
  // components table has more vertical room.
  const [headerOpen, setHeaderOpen] = React.useState(!!row._new);

  // Confirmation wrapper around removeComponent — components are only
  // persisted on Save, but an accidental click still blows away the row
  // out from under the user. A quick confirm keeps it explicit.
  async function confirmRemove(idx, c) {
    const label = c.name || c.code || 'this row';
    const ok = await toast.confirm({
      title: 'Delete component?',
      body: `Remove "${label}" from the schema? This takes effect when you click Save Schema.`,
      confirmLabel: 'Delete component',
      danger: true,
    });
    if (ok) removeComponent(idx);
  }

  return (
    <div style={{ display:'grid', gridTemplateColumns: showSim ? 'minmax(0,1fr) 360px' : 'minmax(0,1fr)', gap:16 }}>
      <div>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
          <button className="btn btn-secondary" onClick={onCancel}>{'\u2190'} Back</button>
          <h2 style={{ margin:0, fontSize:18, flex:1 }}>
            {row._new ? 'New Pricing Schema' : 'Edit Pricing Schema'}
            {row.name && !row._new && (
              <span style={{ color:'#94A3B8', fontWeight:400, fontSize:14, marginLeft:6 }}>— {row.name}</span>
            )}
          </h2>
          <button className="btn btn-secondary" onClick={() => setShowSim(s => !s)}>
            {showSim ? 'Hide Simulator' : 'Show Simulator'}
          </button>
          {!row._new && row.id && (
            <button className="btn btn-secondary"
              onClick={() => { window.location.hash = `#/pricing-schemas/${row.id}/component-masters`; }}
              title="Maintain component-wise rates for this schema. Schema is defaulted and locked.">
              Component Masters
            </button>
          )}
          <button className="btn btn-primary" onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Schema'}
          </button>
        </div>

        {err && (
          <div style={{ background:'rgba(239,68,68,0.12)', color:'#EF4444',
                        padding:'10px 14px', borderRadius:6, marginBottom:12, fontSize:13 }}>{err}</div>
        )}

        <div style={cardStyle}>
          {/* Accordion header — click to fold/unfold. When folded we show a
              one-line summary so the user still sees what they're editing. */}
          <button type="button" onClick={() => setHeaderOpen(o => !o)}
            style={accHeaderStyle}
            title={headerOpen ? 'Fold schema header to give the components more room' : 'Expand schema header'}>
            <Chevron open={headerOpen} />
            <span style={{ fontSize:13, fontWeight:600, color:'#E5E7EB', marginLeft:8 }}>Schema Details</span>
            {!headerOpen && (
              <span style={{ marginLeft:12, fontSize:12, color:'#94A3B8', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {(row.code || '—')} · {(row.name || '(unnamed)')} · {row.schema_type || '—'} · <em style={{ color: row.status==='Active'?'#10B981':'#94A3B8' }}>{row.status || '—'}</em>
              </span>
            )}
          </button>
          {headerOpen && (
            <div style={{ marginTop:12 }}>
              <div className="form-row">
                <FormField label="Code" name="code" value={row.code} onChange={(n,v) => setRow(p => ({...p, code:v}))} required />
                <FormField label="Name" name="name" value={row.name} onChange={(n,v) => setRow(p => ({...p, name:v}))} required />
                <FormField label="Type" name="schema_type" type="select" value={row.schema_type}
                  onChange={(n,v) => setRow(p => ({...p, schema_type:v}))} options={SCHEMA_TYPES} />
              </div>
              <div className="form-row">
                <FormField label="Valid From" name="valid_from" type="date" value={row.valid_from}
                  onChange={(n,v) => setRow(p => ({...p, valid_from:v}))} />
                <FormField label="Valid To" name="valid_to" type="date" value={row.valid_to}
                  onChange={(n,v) => setRow(p => ({...p, valid_to:v}))} />
                <FormField label="Status" name="status" type="select" value={row.status}
                  onChange={(n,v) => setRow(p => ({...p, status:v}))} options={STATUS_OPTIONS} />
              </div>
              <FormField label="Description" name="description" type="textarea" value={row.description || ''}
                onChange={(n,v) => setRow(p => ({...p, description:v}))} />
            </div>
          )}
        </div>

        <div style={{ ...cardStyle, marginTop:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <h3 style={{ margin:0, fontSize:14 }}>
              Components <span style={{ color:'#94A3B8', fontSize:12, marginLeft:6 }}>({components.length})</span>
            </h3>
            <button className="btn btn-sm" onClick={addComponent}>+ Add Component</button>
          </div>

          {components.length === 0 ? (
            <div style={emptyStyle}>No components yet. Click + Add Component to start. Add a Base row first, then taxes or discounts that reference <code>base</code>.</div>
          ) : (
            <div style={{ overflowX:'auto', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8 }}>
              <table style={tblStyle}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width:50  }} title="Sequence — controls computation order.">Seq</th>
                    <th style={{ ...thStyle, width:80  }} title="Short code used in formulas (e.g. BASE, CGST).">Code</th>
                    <th style={{ ...thStyle, width:140 }} title="Human-readable name.">Name</th>
                    <th style={{ ...thStyle, width:140 }} title="Formula. Reference base or earlier component codes. e.g. base * 0.18, base + cgst + sgst">Formula</th>
                    <th style={{ ...thStyle, width:122 }} title="Category — drives whether the component is included in billing (Withholding Tax is excluded from billing and is used only on AR/AP receipts).">Category</th>
                    <th style={{ ...thStyle, width:112 }} title="Type — drives which summary chip the component flows into (Base, Indirect Tax, Direct Tax, Grand Total).">Type</th>
                    <th style={{ ...thStyle, width:64, textAlign:'right' }} title="Rate % (optional — used when Formula is blank).">Rate %</th>
                    <th style={{ ...thStyle, width:96 }} title="GL to debit on invoice / PO posting.">Debit GL</th>
                    <th style={{ ...thStyle, width:96 }} title="GL to credit on invoice / PO posting.">Credit GL</th>
                    <th style={{ ...thStyle, width:96, textAlign:'right' }} title="Actions.">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {components.map((c, idx) => {
                    const cat  = CATEGORY_COLOR[c.category] || CATEGORY_COLOR.tax;
                    const key  = c._key || c.id || idx;
                    const open = !!detailsOpen[key];
                    return (
                      <React.Fragment key={key}>
                        <tr style={{ borderTop:'1px solid rgba(255,255,255,0.06)' }}>
                          <td style={tdStyle}><input type="number" value={c.seq} onChange={e => updateComponent(idx,'seq',e.target.value)} style={{ ...inputStyle, width:'100%' }} /></td>
                          {/* Code is force-uppercased on input — matches how
                              codes appear elsewhere (BASE, CGST, TDS) and
                              keeps the formula resolver predictable. */}
                          <td style={tdStyle}><input type="text"   value={c.code||''} onChange={e => updateComponent(idx,'code', String(e.target.value || '').toUpperCase())} style={{ ...inputStyle, width:'100%' }} placeholder="BASE" /></td>
                          <td style={tdStyle}><input type="text"   value={c.name} onChange={e => updateComponent(idx,'name',e.target.value)} style={{ ...inputStyle, width:'100%' }} placeholder="CGST @ 9%" /></td>
                          <td style={tdStyle}>
                            {/* Formula is force-uppercased on input. The
                                evaluator lowercases the expression before
                                exec, so users can type any case and
                                references like BASE / cgst / Sgst all
                                resolve correctly. */}
                            <input type="text" value={c.formula||''} onChange={e => updateComponent(idx,'formula', String(e.target.value || '').toUpperCase())}
                              style={{ ...inputStyle, width:'100%', fontFamily:'ui-monospace, monospace' }}
                              placeholder="BASE * 0.09" />
                          </td>
                          <td style={tdStyle}>
                            <select value={c.category} onChange={e => {
                              const newCat = e.target.value;
                              updateComponent(idx, 'category', newCat);
                              // If pricing_type is unset, seed it from the new category so
                              // the chips stay in sync without an extra click.
                              if (!c.pricing_type) {
                                const t = defaultTypeFromCategory(newCat);
                                if (t) updateComponent(idx, 'pricing_type', t);
                              }
                            }}
                              style={{ ...inputStyle, width:'100%', color:cat.color, background:cat.bg }}>
                              {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </td>
                          <td style={tdStyle}>
                            <select value={c.pricing_type || defaultTypeFromCategory(c.category) || ''}
                              onChange={e => updateComponent(idx, 'pricing_type', e.target.value || null)}
                              style={{ ...inputStyle, width:'100%' }}>
                              <option value="">— Select —</option>
                              {PRICING_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </td>
                          <td style={{ ...tdStyle, textAlign:'right' }}>
                            <input type="number" step="0.01" value={c.rate_pct||''} onChange={e => updateComponent(idx,'rate_pct',e.target.value)}
                              style={{ ...inputStyle, width:'100%', textAlign:'right' }} placeholder="—" />
                          </td>
                          <td style={tdStyle}><input type="text" value={c.debit_gl||''} onChange={e => updateComponent(idx,'debit_gl',e.target.value)}
                            style={{ ...inputStyle, width:'100%', fontFamily:'ui-monospace, monospace' }} placeholder="2301-CGST-OP" /></td>
                          <td style={tdStyle}><input type="text" value={c.credit_gl||''} onChange={e => updateComponent(idx,'credit_gl',e.target.value)}
                            style={{ ...inputStyle, width:'100%', fontFamily:'ui-monospace, monospace' }} placeholder="4001-Revenue" /></td>
                          <td style={{ ...tdStyle, textAlign:'right' }}>
                            <div style={{ display:'inline-flex', gap:4 }}>
                              <button type="button" onClick={() => moveComponent(idx,-1)} style={iconBtn} title="Move up">↑</button>
                              <button type="button" onClick={() => moveComponent(idx,+1)} style={iconBtn} title="Move down">↓</button>
                              <button type="button" onClick={() => toggleDetails(key)}
                                style={{ ...iconBtn, color: open ? '#00E0FF' : '#94A3B8' }} title={open ? 'Hide details' : 'Show details'}>i</button>
                              <button type="button" onClick={() => confirmRemove(idx, c)}
                                style={deleteBtn} title={`Delete component${c.name ? ' "'+c.name+'"' : ''}`}>
                                <TrashIcon />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {open && (
                          <tr style={{ background:'rgba(0,224,255,0.04)' }}>
                            <td colSpan={10} style={{ padding:'12px 14px', borderTop:'1px dashed rgba(0,224,255,0.2)' }}>
                              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                                <label style={detLblStyle} title="Flat amount added to base (alternative to Rate %).">
                                  <span>Fixed Value (₹)</span>
                                  <input type="number" step="0.01" value={c.fixed_value||''} onChange={e => updateComponent(idx,'fixed_value',e.target.value)}
                                    style={{ ...inputStyle, width:'100%' }} placeholder="e.g. 2500" />
                                </label>
                                <label style={chkLbl} title="Tax input on this component itself? Rarely used.">
                                  <input type="checkbox" checked={!!c.is_taxable} onChange={e => updateComponent(idx,'is_taxable',e.target.checked)} /> Taxable itself
                                </label>
                                <label style={chkLbl} title="Visible on invoices / contract summary.">
                                  <input type="checkbox" checked={!!c.is_visible} onChange={e => updateComponent(idx,'is_visible',e.target.checked)} /> Visible on invoice
                                </label>
                              </div>
                              <label style={{ ...detLblStyle, marginTop:10 }}>
                                <span>Internal Notes</span>
                                <input type="text" value={c.notes||''} onChange={e => updateComponent(idx,'notes',e.target.value)}
                                  style={{ ...inputStyle, width:'100%' }} placeholder="Admin-only notes" />
                              </label>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showSim && (
        <div style={{ position:'sticky', top:16, alignSelf:'start' }}>
          <div style={cardStyle}>
            <h3 style={{ margin:0, fontSize:14, marginBottom:10 }}>Simulator</h3>
            <label style={{ fontSize:12, color:'#94A3B8' }}>
              Contract Base (₹)
              <input type="number" value={simBase} onChange={e => setSimBase(Number(e.target.value)||0)}
                style={{ ...inputStyle, marginTop:4 }} />
            </label>

            {/* Chips drive off pricing_type — each chip is the sum of
                components whose Type matches it. INDIRECT TAX rolls up
                GST/VAT/levy lines; DIRECT TAX rolls up TDS / withholding
                income tax lines; GRAND TOTAL rolls up explicit total
                rows the user marks as Type=Grand Total. */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, margin:'14px 0' }}>
              <Totl label="Base"         value={simulation.totals.base}         color="#3B8FF4" />
              <Totl label="Indirect Tax" value={simulation.totals.indirect_tax} color="#F59E0B" />
              <Totl label="Direct Tax"   value={simulation.totals.direct_tax}   color="#F472B6" />
              <Totl label="Grand Total"  value={simulation.totals.grand_total}  color="#10B981" big />
            </div>

            <div style={{ maxHeight:400, overflowY:'auto', border:'1px solid rgba(255,255,255,0.06)', borderRadius:6, padding:6 }}>
              {simulation.results.length === 0 ? (
                <div style={{ color:'#64748B', fontSize:12, padding:12, textAlign:'center' }}>
                  Add components to see values
                </div>
              ) : simulation.results.map((r, i) => {
                const cat = CATEGORY_COLOR[r.category] || CATEGORY_COLOR.tax;
                const v = Number(r._computed) || 0;
                const tag = r._source === 'master'
                  ? `L${r._matchLevel || '?'}`
                  : (r._source === 'formula' ? 'fx' : '—');
                const tagColor = r._source === 'master' ? '#10B981' : '#94A3B8';
                return (
                  <div key={i} style={{
                    display:'grid', gridTemplateColumns:'1fr auto auto', gap:8, padding:'4px 6px',
                    fontSize:12, borderBottom:'1px solid rgba(255,255,255,0.04)', alignItems:'center',
                  }}>
                    <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      <span style={{ display:'inline-block', width:8, height:8, borderRadius:2,
                        background:cat.color, marginRight:6, opacity:0.8 }}></span>
                      {r.name || r.code}
                    </div>
                    <span style={{
                      fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:4,
                      background:'rgba(255,255,255,0.04)', color: tagColor, letterSpacing:'0.4px',
                    }} title={r._source === 'master' ? 'Resolved from Component Master' : 'Computed from formula'}>
                      {tag}
                    </span>
                    <div style={{ fontFamily:'ui-monospace, monospace', color: v>=0 ? '#E5E7EB' : '#EF4444' }}>
                      {fmtINR(v)}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize:10.5, color:'#64748B', marginTop:8, lineHeight:1.4 }}>
              Master rates (when present) override formulas. Without contract context only Level 3 masters can match — to preview L1/L2 rates, open a real contract and check the Pricing Computation panel.
              Formulas can reference <code>base</code> or any earlier component by its lowercase code. Helpers: <code>min, max, round</code>.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Totl({ label, value, color, big }) {
  return (
    <div style={{
      background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)',
      borderRadius:6, padding:'8px 10px',
    }}>
      <div style={{ fontSize:10, color:'#94A3B8', marginBottom:2, textTransform:'uppercase', letterSpacing:0.5 }}>{label}</div>
      <div style={{ color, fontSize: big?15:13, fontWeight:700, fontFamily:'ui-monospace, monospace' }}>{fmtINR(value)}</div>
    </div>
  );
}

const cardStyle  = { background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:10, padding:16 };
// Tighter padding so values like "9.000" or "INDIRECT TAX" don't truncate
// in the narrow table cells.
const inputStyle = { padding:'4px 8px', borderRadius:5, border:'1px solid rgba(255,255,255,0.10)',
                     background:'rgba(255,255,255,0.04)', color:'#E5E7EB', fontSize:12, outline:'none', width:'100%',
                     boxSizing:'border-box' };
const iconBtn    = { width:26, height:26, padding:0, display:'inline-flex', alignItems:'center', justifyContent:'center',
                     borderRadius:5, border:'1px solid rgba(255,255,255,0.08)', background:'rgba(255,255,255,0.04)',
                     color:'#94A3B8', cursor:'pointer', fontSize:14 };
const chkLbl     = { display:'inline-flex', alignItems:'center', gap:5, cursor:'pointer', fontSize:12, color:'#94A3B8' };
const emptyStyle = { padding:24, textAlign:'center', color:'#64748B', fontSize:13,
                     background:'rgba(255,255,255,0.03)', borderRadius:8, border:'1px dashed rgba(255,255,255,0.08)' };

// Narrower min-width so the table fits inside the section card with the
// simulator open (main column ≈ container − 360px). Combined with the
// wrapper's `overflow-x: auto`, columns past Type scroll horizontally
// when the available width can't fit them all.
const tblStyle = { width:'100%', minWidth: 1000, borderCollapse:'separate', borderSpacing:0, tableLayout:'fixed', fontSize:12 };
const thStyle  = { padding:'10px 10px', textAlign:'left',
                   background:'rgba(255,255,255,0.04)',
                   color:'#94A3B8', fontSize:11, fontWeight:600,
                   textTransform:'uppercase', letterSpacing:'0.4px',
                   borderBottom:'1px solid rgba(255,255,255,0.10)',
                   cursor:'help' };
const tdStyle  = { padding:'6px 8px', verticalAlign:'middle' };
const detLblStyle = { display:'flex', flexDirection:'column', gap:4, fontSize:11, color:'#94A3B8', letterSpacing:'0.2px' };

// Clickable accordion header — full-width, transparent, minimal-affordance.
const accHeaderStyle = {
  display:'flex', alignItems:'center', gap:2,
  width:'100%', padding:'2px 4px', margin:0,
  background:'transparent', border:'none', cursor:'pointer', textAlign:'left',
};

function Chevron({ open }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
         style={{ color:'#94A3B8', transition:'transform 0.2s ease', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

// A more visible delete button than the subtle × — uses a red tint so the
// user can spot the destructive action among the icon cluster.
const deleteBtn = {
  width:28, height:28, padding:0, display:'inline-flex', alignItems:'center', justifyContent:'center',
  borderRadius:5, border:'1px solid rgba(239,68,68,0.35)',
  background:'rgba(239,68,68,0.10)', color:'#EF4444', cursor:'pointer',
};
function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}
